import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import IconButtonV2 from '../IconButtonV2';
import LoaderSkeleton from '../LoaderSkeleton';
import Spinner from '../Spinner';
import TextArea from '../TextArea';
import { ArrowRightIcon } from '../../icons';

import styles from './_index.module.scss';

const SEGMENT_DELAY_MS = 800;
type SegmentTimeoutId = ReturnType<typeof setTimeout>;

const splitAssistantMessageSegments = (value: string) =>
  value
    .split(/\n{2,}/)
    .map((segment) => segment.trim())
    .filter(Boolean);

export type ChatRole = 'user' | 'assistant';

export type ChatMessageVariant = 'default' | 'status';

export type ChatMessage = {
  content: ReactNode;
  isStreaming?: boolean;
  id: string;
  role: ChatRole;
  variant?: ChatMessageVariant;
};

export type ChatWindowMarkdownMode = 'assistant' | 'all' | 'none';

export interface ChatWindowProps {
  autoScroll?: boolean;
  className?: string;
  footerHint?: ReactNode;
  headerTitle?: string;
  inputDisabled?: boolean;
  inputValue?: string;
  isAgentStreaming?: boolean;
  messages?: ChatMessage[];
  onChangeInput?: (value: string) => void;
  onSubmit?: (value: string) => void;
  renderMarkdown?: ChatWindowMarkdownMode;
  submitDisabled?: boolean;
}

const ChatWindow = ({
  autoScroll = true,
  className,
  footerHint,
  headerTitle,
  inputDisabled = false,
  inputValue = '',
  isAgentStreaming = false,
  messages = [],
  onChangeInput,
  onSubmit,
  renderMarkdown = 'assistant',
  submitDisabled = false,
}: ChatWindowProps) => {
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [pendingInput, setPendingInput] = useState<string | null>(null);
  const [visibleSegmentCounts, setVisibleSegmentCounts] = useState<Record<string, number>>({});

  const generatedMessageCounter = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const forceScrollRef = useRef(false);
  const segmentCacheRef = useRef<Record<string, string[]>>({});
  const segmentTimeoutsRef = useRef<Record<string, SegmentTimeoutId[]>>({});

  const combinedMessages = useMemo(
    () => [...messages, ...localMessages],
    [messages, localMessages]
  );

  const previousMessagesRef = useRef<string[]>([]);

  useEffect(() => {
    const nextMessageIds = messages.map((message) => message.id);
    const previousMessageIds = previousMessagesRef.current;
    const hasNewRemoteMessage =
      nextMessageIds.length !== previousMessageIds.length ||
      nextMessageIds.some((id, index) => id !== previousMessageIds[index]);

    if (hasNewRemoteMessage && nextMessageIds.length) {
      forceScrollRef.current = true;
    }

    previousMessagesRef.current = nextMessageIds;
  }, [messages]);

  const assistantMessageCount = useMemo(
    () => messages?.filter((m) => m.role === 'assistant').length,
    [messages]
  );

  const assistantCountSnapshotRef = useRef<number>(assistantMessageCount);
  const isAgentStreamingSnapshotRef = useRef<boolean>(isAgentStreaming);

  const hasStreamingAssistantMessage = combinedMessages.some(
    (message) => message.role === 'assistant' && message.isStreaming
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const assistantMessages = combinedMessages.filter(
      (message) =>
        message.role === 'assistant' &&
        typeof message.content === 'string' &&
        message.content.trim()
    );

    const activeIds = new Set<string>();

    assistantMessages.forEach((message) => {
      const contentString = String(message.content);
      const segments = splitAssistantMessageSegments(contentString);
      segmentCacheRef.current[message.id] = segments;
      activeIds.add(message.id);

      setVisibleSegmentCounts((previous) => {
        if (previous[message.id]) return previous;
        return { ...previous, [message.id]: 1 };
      });

      if (!segmentTimeoutsRef.current[message.id] && segments.length > 1) {
        segmentTimeoutsRef.current[message.id] = segments.slice(1).map((_, index) =>
          setTimeout(
            () => {
              setVisibleSegmentCounts((previous) => {
                const currentCount = previous[message.id] ?? 1;
                if (currentCount >= segments.length) return previous;
                return { ...previous, [message.id]: currentCount + 1 };
              });
            },
            SEGMENT_DELAY_MS * (index + 1)
          )
        );
      }
    });

    Object.keys(segmentCacheRef.current).forEach((messageId) => {
      if (activeIds.has(messageId)) return;
      const timers = segmentTimeoutsRef.current[messageId];
      if (timers) {
        timers.forEach((timeoutId) => clearTimeout(timeoutId));
        delete segmentTimeoutsRef.current[messageId];
      }
      delete segmentCacheRef.current[messageId];
      setVisibleSegmentCounts((previous) => {
        if (!(messageId in previous)) return previous;
        const next = { ...previous };
        delete next[messageId];
        return next;
      });
    });
  }, [combinedMessages]);

  useEffect(() => {
    return () => {
      Object.values(segmentTimeoutsRef.current).forEach((timeouts) => {
        timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      });
    };
  }, []);

  const messagesToRender = useMemo(() => {
    const shouldInsertStreamingPlaceholder = isAgentStreaming && !hasStreamingAssistantMessage;
    if (!shouldInsertStreamingPlaceholder) return combinedMessages;

    const streamingPlaceholder: ChatMessage = {
      content: '',
      id: 'assistant-streaming-placeholder',
      isStreaming: true,
      role: 'assistant',
      variant: 'status',
    };

    return [...combinedMessages, streamingPlaceholder];
  }, [combinedMessages, hasStreamingAssistantMessage, isAgentStreaming]);

  const showStreamingSkeleton = isAgentStreaming && !hasStreamingAssistantMessage;
  const shouldShowInputLoader = awaitingResponse || showStreamingSkeleton;

  const isSubmitDisabled = inputDisabled || submitDisabled || awaitingResponse;
  const hasInputText = inputValue.trim().length > 0;
  const inputDisplayValue = awaitingResponse ? (pendingInput ?? inputValue) : inputValue;
  const isTextAreaDisabled = inputDisabled || awaitingResponse;

  const shouldRenderMarkdownForRole = (role: ChatRole) => {
    if (renderMarkdown === 'none') return false;
    if (renderMarkdown === 'all') return true;
    return role === 'assistant';
  };

  useEffect(() => {
    if (!messages.length) {
      setLocalMessages([]);
    }
  }, [messages]);

  useEffect(() => {
    if (!awaitingResponse && localMessages.length) {
      setLocalMessages([]);
    }
  }, [awaitingResponse, localMessages.length]);

  useEffect(() => {
    if (!autoScroll) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const threshold = 80;
    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    const isNearBottom = distanceFromBottom <= threshold;
    const shouldForceScroll = forceScrollRef.current;

    if (shouldForceScroll || isNearBottom) {
      container.scrollTop = container.scrollHeight;
      forceScrollRef.current = false;
    }
  }, [autoScroll, combinedMessages, isAgentStreaming]);

  useEffect(() => {
    const prevAssistantCount = assistantCountSnapshotRef.current;
    const prevIsAgentStreaming = isAgentStreamingSnapshotRef.current;

    const assistantArrived = awaitingResponse && assistantMessageCount > prevAssistantCount;
    const agentFinished = awaitingResponse && prevIsAgentStreaming && !isAgentStreaming;

    if (assistantArrived || agentFinished) {
      setAwaitingResponse(false);
      setPendingInput(null);
    }

    assistantCountSnapshotRef.current = assistantMessageCount;
    isAgentStreamingSnapshotRef.current = isAgentStreaming;
  }, [assistantMessageCount, awaitingResponse, isAgentStreaming]);

  const handleChangeInput = (value: string) => onChangeInput?.(value);

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    const nextMessage: ChatMessage = {
      content: trimmedValue,
      id: `local-user-${Date.now()}-${generatedMessageCounter.current}`,
      role: 'user',
    };
    generatedMessageCounter.current += 1;

    assistantCountSnapshotRef.current = assistantMessageCount;
    isAgentStreamingSnapshotRef.current = isAgentStreaming;

    setPendingInput(trimmedValue);
    setAwaitingResponse(true);
    setLocalMessages((previousMessages) => [...previousMessages, nextMessage]);
    forceScrollRef.current = true;

    onSubmit?.(trimmedValue);
    onChangeInput?.('');
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    handleSubmit();
  };

  return (
    <div className={classNames(styles['chat-window'], className)}>
      {headerTitle && (
        <div className={styles['chat-header']} data-testid="chat-header">
          <h3> {headerTitle} </h3>
        </div>
      )}
      <div className={styles['chat-body']} ref={scrollContainerRef}>
        <div aria-live="polite" className={styles['chat-messages']}>
          <div className={styles['message-list']}>
            {messagesToRender.map((message) => {
              const isStatusMessage = message.variant === 'status';
              const shouldShowMessageLoader =
                message.role === 'assistant' && Boolean(message.isStreaming) && isAgentStreaming;
              const isStringContent = typeof message.content === 'string';
              const segments =
                isStringContent && typeof message.content === 'string'
                  ? (segmentCacheRef.current[message.id] ??
                    splitAssistantMessageSegments(message.content))
                  : null;
              if (isStringContent && segments && !segmentCacheRef.current[message.id]) {
                segmentCacheRef.current[message.id] = segments;
              }
              const totalSegments = segments?.length ?? 0;
              const visibleSegmentCount =
                segments && totalSegments > 0
                  ? (visibleSegmentCounts[message.id] ?? totalSegments)
                  : null;
              const hasPendingSegments =
                segments && visibleSegmentCount !== null && visibleSegmentCount < totalSegments;
              const visibleContent =
                segments && visibleSegmentCount !== null
                  ? segments.slice(0, visibleSegmentCount).join('\n\n')
                  : isStringContent
                    ? (message.content as string)
                    : null;
              const contentToRender =
                isStringContent && typeof visibleContent === 'string'
                  ? visibleContent
                  : message.content;
              return (
                <div
                  className={classNames(styles['message'], styles[`is-${message.role}`], {
                    [styles['message-status']]: isStatusMessage,
                  })}
                  key={message.id}
                >
                  <div className={styles['message-bubble']}>
                    <div className={styles['message-content']}>
                      {typeof contentToRender === 'string' ? (
                        shouldRenderMarkdownForRole(message.role) ? (
                          <ReactMarkdown>{contentToRender}</ReactMarkdown>
                        ) : (
                          contentToRender
                        )
                      ) : (
                        message.content
                      )}

                      {hasPendingSegments && (
                        <div className={styles['message-content-loader']}>
                          <LoaderSkeleton
                            className={styles['message-loader']}
                            height={50}
                            width={240}
                          >
                            <rect height="8" rx="4" ry="4" width="200" x="0" y="6" />
                            <rect height="8" rx="4" ry="4" width="160" x="0" y="24" />
                            <rect height="8" rx="4" ry="4" width="120" x="0" y="42" />
                          </LoaderSkeleton>
                        </div>
                      )}

                      {shouldShowMessageLoader && (
                        <div className={styles['message-content-loader']}>
                          <LoaderSkeleton
                            className={styles['message-loader']}
                            height={50}
                            width={240}
                          >
                            <rect height="8" rx="4" ry="4" width="200" x="0" y="6" />
                            <rect height="8" rx="4" ry="4" width="160" x="0" y="24" />
                            <rect height="8" rx="4" ry="4" width="120" x="0" y="42" />
                          </LoaderSkeleton>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles['chat-footer']}>
        {footerHint && <div className={styles['chat-footer-hint']}>{footerHint}</div>}

        <div
          className={classNames(styles['chat-input-field'], {
            [styles['chat-input-field-loading']]: shouldShowInputLoader,
          })}
        >
          <div className={styles['chat-input-textarea']}>
            <TextArea
              autoGrow
              className={styles['chat-input-control']}
              isDisabled={isTextAreaDisabled}
              onChange={handleChangeInput}
              onKeyDown={handleInputKeyDown}
              placeholder="How can I help?"
              rows={1}
              value={inputDisplayValue}
            />
          </div>

          {shouldShowInputLoader ? (
            <span
              className={classNames(
                styles['chat-input-submit'],
                styles['chat-input-submit-spinner']
              )}
              data-testid="chat-input-submit-spinner"
            >
              <Spinner className={styles['chat-input-spinner']} />
            </span>
          ) : (
            <span
              className={classNames(styles['chat-input-submit'], {
                [styles['chat-input-submit-filled']]: hasInputText,
              })}
            >
              <IconButtonV2
                aria-label="Submit prompt"
                disabled={isSubmitDisabled || !hasInputText}
                icon={<ArrowRightIcon />}
                onClick={handleSubmit}
                size="small"
                variant="subtle"
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
